"use client";

import { c as _c } from "react/compiler-runtime";
import { memo, useEffect, useRef, useState, useCallback } from "react";
import Link from "@/components/ui/Link";
import { usePathname } from "next/navigation";
import dynamic from "next/dynamic";
import Image from "next/image";
// import { useTheme } from "next-themes";
import { usePublicAuth } from "@/lib/auth/PublicAuthContext";
import { getMenuGroups, getHeaderFooterConfig, MenuGroupItem } from "@/lib/api";
import { FaEllipsisH, FaChevronDown, FaSearch, FaUser, FaTimes, FaBookmark } from "react-icons/fa";
import { HiMenu } from "react-icons/hi";
import SocialIcons from "./SocialIcons";
import { useDevice } from '@/contexts/DeviceContext';
import type { HeaderFooterConfig, GroupLinkEntry } from "@/lib/local-store/types";
import { DEFAULT_HEADER_FOOTER_CONFIG } from "@/lib/local-store/defaultHeaderFooter";
import { resolveAssetUrl } from "@/lib/utils";
import { format as formatDateFns } from "date-fns";
import { ta, enUS } from "date-fns/locale";

// Fallback icons, used only for the two known default-config buttons when a
// synced button has no icon of its own (e.g. before the first sync).
const HEADER_BUTTON_ICONS: Record<string, {
  src: string;
  alt: string;
}> = {
  'btn-1': {
    src: "https://media.dinamani.com/dinamani/2026-02-27/0o5b196u/crownicon.png",
    alt: "Crown"
  },
  'btn-2': {
    src: "https://media.dinamani.com/dinamani/2026-02-27/36zobb67/papericon.png",
    alt: "Paper"
  }
};

// Relative URLs and the main dinamani.com host are internal; any other host
// (including subdomains like epaper.dinamani.com) opens in a new tab.
function isExternalUrl(raw: string): boolean {
  if (!/^https?:\/\//i.test(raw)) return false;
  try {
    const {
      hostname
    } = new URL(raw);
    return hostname !== "www.dinamani.com" && hostname !== "dinamani.com";
  } catch {
    return false;
  }
}

// Lazy load heavy components (only needed on interaction)
const MegaMenu = dynamic(() => import("./MegaMenu"), {
  ssr: false
});
const SearchLayoutSection = dynamic(() => import("./SearchLayoutSection"), {
  ssr: false
});
const LoginRegisterForm = dynamic(() => import("../component_form/LoginRegisterForm"), {
  ssr: false,
  loading: () => <div className="p-8 text-center">Loading...</div>
});
const OTPForm = dynamic(() => import("../component_form/OTPForm"), {
  ssr: false,
  loading: () => <div className="p-8 text-center">Loading...</div>
});
const ProfileMenu = dynamic(() => import("../component_unique/ProfileIconMenu"), {
  ssr: false,
  loading: () => <div className="p-4 text-center">Loading...</div>
});
const DATE_LOCALES: Record<string, typeof ta> = {
  ta,
  en: enUS
};

// date-fns renders the "a" token in Tamil as முற்பகல் / பிற்பகல்; show plain AM / PM in the header instead.
const TAMIL_DAY_PERIODS: Record<string, string> = {
  "முற்பகல்": "AM",
  "பிற்பகல்": "PM"
};
const TAMIL_DAY_PERIOD_RE = /முற்பகல்|பிற்பகல்/g;

// Renders the header clock using the format/locale tokens synced from
// Builder (e.g. "dd MMMM yyyy, hh:mm a" + "ta"), falling back to English if
// an unrecognized locale code or a malformed format string is supplied.
function formatHeaderDateTime(dateTime: {
  format: string;
  locale: string;
}): string {
  const locale = DATE_LOCALES[dateTime.locale] || enUS;
  try {
    return formatDateFns(new Date(), dateTime.format, {
      locale
    }).replace(TAMIL_DAY_PERIOD_RE, word => TAMIL_DAY_PERIODS[word]);
  } catch {
    return formatDateFns(new Date(), "dd MMMM yyyy, hh:mm a", {
      locale: enUS
    });
  }
}
function GroupSitesStrip(t0) {
  const $ = _c(8);
  const {
    links,
    className: t1,
    linkClassName: t2
  } = t0;
  const className = t1 === undefined ? "" : t1;
  const linkClassName = t2 === undefined ? "" : t2;
  const t3 = `flex items-center whitespace-nowrap ${className}`;
  let t4;
  if ($[0] !== linkClassName || $[1] !== links) {
    let t5;
    if ($[3] !== linkClassName) {
      t5 = (site, idx) => <span key={site.id} className="flex items-center">{idx > 0 && <span className="mx-1.5 text-DCDCDC" aria-hidden="true">|</span>}<Link href={site.url} target={site.openInNewTab === false ? undefined : "_blank"} rel={site.openInNewTab === false ? undefined : "noopener noreferrer"} className={`tracking-wide hover:text-012061 transition-colors ${linkClassName}`}>{site.label}</Link></span>;
      $[3] = linkClassName;
      $[4] = t5;
    } else {
      t5 = $[4];
    }
    t4 = links.map(t5);
    $[0] = linkClassName;
    $[1] = links;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[5] !== t3 || $[6] !== t4) {
    t5 = <div className={t3}>{t4}</div>;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
}

// Hierarchical menu item type
interface HierarchicalMenuItem extends MenuGroupItem {
  children?: HierarchicalMenuItem[];
}

// Build hierarchical menu from flat items
function buildMenuHierarchy(items: MenuGroupItem[]): HierarchicalMenuItem[] {
  const itemMap = new Map<number, HierarchicalMenuItem>();
  const rootItems: HierarchicalMenuItem[] = [];

  // First pass: create all items with children array
  items.forEach(item => {
    if (!item.title || !item.id) return;
    itemMap.set(item.id, {
      ...item,
      children: []
    });
  });

  // Second pass: build hierarchy
  items.forEach(item => {
    if (!item.title || !item.id) return;
    const menuItem = itemMap.get(item.id);
    if (!menuItem) return;
    if (item["parent-id"] && itemMap.has(item["parent-id"])) {
      const parent = itemMap.get(item["parent-id"]);
      if (parent) {
        parent.children = parent.children || [];
        parent.children.push(menuItem);
      }
    } else {
      rootItems.push(menuItem);
    }
  });

  // Recursively remove empty children arrays
  const cleanEmptyChildren = (menuItems: HierarchicalMenuItem[]) => {
    menuItems.forEach(item => {
      if (item.children && item.children.length === 0) {
        delete item.children;
      } else if (item.children) {
        cleanEmptyChildren(item.children);
      }
    });
  };
  cleanEmptyChildren(rootItems);
  return rootItems;
}
interface HeaderProps {
  trendingTags?: string[];
  serverMenuData?: {
    "menu-groups"?: Record<string, any>;
  } | null;
  headerFooterConfig?: HeaderFooterConfig | null;
}

// Helper to initialize menu items from server data (used for SSR)
function initMenuItemsFromServerData(serverMenuData: HeaderProps['serverMenuData']): HierarchicalMenuItem[] {
  if (!serverMenuData?.["menu-groups"]) return [];
  const items = serverMenuData["menu-groups"]?.default?.items ?? [];
  const cleaned = items.filter((it: any) => Boolean(it?.title) && Boolean(it?.url || it?.data?.link));
  return buildMenuHierarchy(cleaned);
}

// Build the mobile overlay tree for one menu group (looked up by slug, falling back to its display name).
// - items without a title are dropped, titles are trimmed and siblings follow `rank`
// - children attach through `parent-id`; a missing parent, self-parent or loop promotes the item to top level
// - an item is kept only if it has a link or still has visible children, so empty placeholders and
//   dead chevrons never render (a link-less parent with children stays as an expandable heading)
function buildMobileMenuTree(groups: Record<string, any> | undefined | null, slug: string, name: string): HierarchicalMenuItem[] {
  if (!groups) return [];
  const group = groups[slug] ?? Object.values(groups).find((g: any) => g?.name === name);
  const items: MenuGroupItem[] = (group as any)?.items ?? [];
  const nodes = new Map<number, HierarchicalMenuItem>();
  for (const it of items) {
    const title = typeof it?.title === "string" ? it.title.trim() : "";
    if (!title || !it?.id || nodes.has(it.id)) continue;
    nodes.set(it.id, {
      ...it,
      title,
      children: []
    });
  }
  const parentOf = (node: HierarchicalMenuItem) => {
    const pid = node["parent-id"];
    return pid && pid !== node.id ? nodes.get(pid) ?? null : null;
  };
  const createsLoop = (node: HierarchicalMenuItem) => {
    const seen = new Set<number>([node.id]);
    for (let cur = parentOf(node); cur; cur = parentOf(cur)) {
      if (seen.has(cur.id)) return true;
      seen.add(cur.id);
    }
    return false;
  };
  const roots: HierarchicalMenuItem[] = [];
  const byRank = (a: HierarchicalMenuItem, b: HierarchicalMenuItem) => (a.rank ?? 0) - (b.rank ?? 0);
  for (const node of [...nodes.values()].sort(byRank)) {
    const parent = createsLoop(node) ? null : parentOf(node);
    (parent ? parent.children! : roots).push(node);
  }
  const prune = (list: HierarchicalMenuItem[]): HierarchicalMenuItem[] => list.filter(node => {
    const visibleChildren = prune(node.children ?? []);
    if (visibleChildren.length > 0) node.children = visibleChildren;else delete node.children;
    return Boolean(node.children || node.url || node.data?.link);
  });
  return prune(roots);
}
type ResolveMenuHref = (item: MenuGroupItem) => {
  href: string;
  external: boolean;
} | null;

// Accordion list for the mobile overlay: one open branch per level, recursive for nested children.
function MobileMenuList(t0) {
  const $ = _c(18);
  const {
    items,
    depth: t1,
    resolveHref,
    onNavigate
  } = t0;
  const depth = t1 === undefined ? 0 : t1;
  const [expandedId, setExpandedId] = useState(null);
  const isRoot = depth === 0;
  const linkClass = isRoot ? "text-14-tamil-600 text-012061 font-semibold block" : "text-13-tamil-400 text-gray-600 hover:text-1B1B1B block";
  const t2 = isRoot ? "space-y-2" : "pl-6 pb-3 space-y-1 border-l-2 border-gray-300 ml-4";
  let t3;
  if ($[0] !== depth || $[1] !== expandedId || $[2] !== isRoot || $[3] !== items || $[4] !== linkClass || $[5] !== onNavigate || $[6] !== resolveHref) {
    let t4;
    if ($[8] !== depth || $[9] !== expandedId || $[10] !== isRoot || $[11] !== linkClass || $[12] !== onNavigate || $[13] !== resolveHref) {
      t4 = item => {
        const resolved = resolveHref(item);
        const hasChildren = Boolean(item.children?.length);
        if (!resolved && !hasChildren) {
          return null;
        }
        const isExpanded = hasChildren && expandedId === item.id;
        const toggle = () => setExpandedId(isExpanded ? null : item.id);
        return <li key={item.id} className={isRoot ? "transition-colors rounded-lg bg-F8F8F8 hover:bg-ECEEF3" : "rounded-lg hover:bg-gray-200"}><div className={`${isRoot ? "p-4" : "p-2"} flex items-center justify-between`}><div className="flex-1">{!resolved ? <button type="button" onClick={toggle} className={`${linkClass} w-full text-left cursor-pointer`}>{item.title}</button> : resolved.external ? <a href={resolved.href} target="_blank" rel="noreferrer" className={linkClass} onClick={onNavigate}>{item.title}</a> : <Link href={resolved.href} className={linkClass} onClick={onNavigate}>{item.title}</Link>}</div>{hasChildren && <button type="button" onClick={toggle} className="p-2 hover:bg-gray-200 rounded transition-colors cursor-pointer" aria-label={isExpanded ? "Collapse submenu" : "Expand submenu"} aria-expanded={isExpanded}><FaChevronDown className={`text-[12px] text-012061 transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`} /></button>}</div>{isExpanded && <MobileMenuList items={item.children} depth={depth + 1} resolveHref={resolveHref} onNavigate={onNavigate} />}</li>;
      };
      $[8] = depth;
      $[9] = expandedId;
      $[10] = isRoot;
      $[11] = linkClass;
      $[12] = onNavigate;
      $[13] = resolveHref;
      $[14] = t4;
    } else {
      t4 = $[14];
    }
    t3 = items.map(t4);
    $[0] = depth;
    $[1] = expandedId;
    $[2] = isRoot;
    $[3] = items;
    $[4] = linkClass;
    $[5] = onNavigate;
    $[6] = resolveHref;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[15] !== t2 || $[16] !== t3) {
    t4 = <ul className={t2}>{t3}</ul>;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  return t4;
}
function HeaderComponent({
  trendingTags = [],
  serverMenuData,
  headerFooterConfig: headerFooterConfigProp
}: HeaderProps) {
  const [headerFooterConfig, setHeaderFooterConfig] = useState<HeaderFooterConfig | null>(headerFooterConfigProp ?? null);
  const cfg = headerFooterConfig ?? DEFAULT_HEADER_FOOTER_CONFIG;

  // Fallback: fetch client-side only if no server-provided config was available
  useEffect(() => {
    if (headerFooterConfigProp) return;
    let cancelled = false;
    (async () => {
      const data = await getHeaderFooterConfig();
      if (!cancelled && data) {
        setHeaderFooterConfig(data);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [headerFooterConfigProp]);
  const [openMenu, setOpenMenu] = useState(false);
  const [menuSource, setMenuSource] = useState<"top" | "bottom" | "compact">("top");
  const [openSearch, setOpenSearch] = useState(false);
  const [openAuth, setOpenAuth] = useState(false);
  const [authPopup, setAuthPopup] = useState<"login" | "otp" | "profile">("login");
  const [profileMenuPos, setProfileMenuPos] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const {
    isDesktop,
    isMobile
  } = useDevice();
  // Initialize menu items directly from server data for instant render (no flash)
  const [desktopMenuItems, setDesktopMenuItems] = useState<HierarchicalMenuItem[]>(() => initMenuItemsFromServerData(serverMenuData));
  // Mobile overlay: hamburger shows "sidebar-menu", 3-dot shows "mega-menu"
  const [mobileSidebarItems, setMobileSidebarItems] = useState<HierarchicalMenuItem[]>(() => buildMobileMenuTree(serverMenuData?.["menu-groups"], "sidebar-menu", "Sidebar Menu"));
  const [mobileMegaItems, setMobileMegaItems] = useState<HierarchicalMenuItem[]>(() => buildMobileMenuTree(serverMenuData?.["menu-groups"], "mega-menu", "Mega Menu"));
  const [activeDropdown, setActiveDropdown] = useState<number | null>(null);
  const [mobileNavDropdown, setMobileNavDropdown] = useState<number | null>(null);
  const [mobileDropdownPosition, setMobileDropdownPosition] = useState<number>(0);
  const pathname = usePathname();

  // Scroll tracking for sticky nav
  const [isNavFixed, setIsNavFixed] = useState(false);

  // const { theme, setTheme, resolvedTheme } = useTheme();
  const {
    isAuthenticated,
    resetAuthState
  } = usePublicAuth();
  const [currentTime, setCurrentTime] = useState<string>('');
  const [mounted, setMounted] = useState(false);
  const userButtonRef = useRef<HTMLButtonElement | null>(null);
  const userButtonRefMobile = useRef<HTMLButtonElement | null>(null);
  const headerRef = useRef<HTMLElement | null>(null);
  const megaMenuRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLDivElement | null>(null);
  const mobileNavDropdownRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    setMounted(true);
  }, []);

  // Close menu/search when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Check if click is outside header and mega menu
      if (openMenu) {
        const isOutsideHeader = headerRef.current && !headerRef.current.contains(target);
        const isOutsideMegaMenu = megaMenuRef.current && !megaMenuRef.current.contains(target);
        if (isOutsideHeader && isOutsideMegaMenu) {
          setOpenMenu(false);
        }
      }

      // Check if click is outside header and search
      if (openSearch) {
        const isOutsideHeader_0 = headerRef.current && !headerRef.current.contains(target);
        const isOutsideSearch = searchRef.current && !searchRef.current.contains(target);
        if (isOutsideHeader_0 && isOutsideSearch) {
          setOpenSearch(false);
        }
      }

      // Check if click is outside mobile nav dropdown
      if (mobileNavDropdown !== null) {
        const isOutsideMobileDropdown = mobileNavDropdownRef.current && !mobileNavDropdownRef.current.contains(target);
        if (isOutsideMobileDropdown) {
          setMobileNavDropdown(null);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openMenu, openSearch, mobileNavDropdown]);

  // Update time only on client to avoid hydration mismatch
  useEffect(() => {
    const update = () => setCurrentTime(formatHeaderDateTime(cfg.header.dateTime));
    update();

    // Update time every minute
    const interval = setInterval(update, 60000);
    return () => clearInterval(interval);
  }, [cfg.header.dateTime.format, cfg.header.dateTime.locale]);
  const toggleSearch = useCallback(() => {
    setOpenSearch(prev => {
      if (!prev) {
        setOpenMenu(false);
        setMobileNavDropdown(null);
      }
      return !prev;
    });
  }, []);

  // const toggleTheme = useCallback(() => {
  //   setTheme(theme === 'dark' ? 'light' : 'dark');
  // }, [theme, setTheme]);

  const isActiveHref = (href: string): boolean => {
    const cleanHref = href.split("?")[0] || "/";
    const normalize = (p: string) => p.length > 1 ? p.replace(/\/+$/, "") : p;
    const a = normalize(pathname || "/");
    const b = normalize(cleanHref);
    return a === b;
  };
  const closeSearch = () => {
    setOpenSearch(false);
  };
  const openAuthModal = () => {
    const nextPopup = isAuthenticated ? "profile" : "login";
    setAuthPopup(nextPopup);
    const buttonEl = isDesktop ? userButtonRef.current : userButtonRefMobile.current;
    if (nextPopup === "profile" && buttonEl) {
      const rect = buttonEl.getBoundingClientRect();
      const desiredWidth = 260;
      const width = Math.min(desiredWidth, Math.max(180, window.innerWidth - 20));
      const left = Math.max(10, Math.min(rect.right - width, window.innerWidth - width - 10));
      const top = Math.min(rect.bottom + 8, window.innerHeight - 10);
      setProfileMenuPos({
        top,
        left
      });
    } else {
      setProfileMenuPos(null);
    }
    setOpenAuth(true);
    setOpenMenu(false);
    setOpenSearch(false);
  };
  const goToOtp = () => {
    setAuthPopup("otp");
    setProfileMenuPos(null);
    setOpenAuth(true);
  };
  const closeAuthModal = () => {
    setOpenAuth(false);
    resetAuthState();
  };
  const handleAuthSuccess = () => {
    setOpenAuth(false);
    resetAuthState();
  };

  // Close menus on route change
  useEffect(() => {
    setOpenMenu(false);
    setOpenSearch(false);
    setActiveDropdown(null);
    setMobileNavDropdown(null);
  }, [pathname]);

  // Fallback: Fetch client-side only if no menu items (server data wasn't available)
  useEffect(() => {
    // Skip if we already have menu items from server data
    if (desktopMenuItems.length > 0) {
      return;
    }

    // Fallback: Fetch client-side
    let cancelled_0 = false;
    (async () => {
      const data_0 = await getMenuGroups();
      const items = data_0?.["menu-groups"]?.default?.items ?? [];
      const cleaned = items.filter(it => Boolean(it?.title) && Boolean(it?.url || it?.data?.link));
      const hierarchicalItems = buildMenuHierarchy(cleaned);
      if (!cancelled_0 && hierarchicalItems.length > 0) {
        setDesktopMenuItems(hierarchicalItems);
      }
    })();
    return () => {
      cancelled_0 = true;
    };
  }, []); // Only run once on mount

  // Fallback: Fetch mobile overlay menus client-side if not available from server
  useEffect(() => {
    if (mobileSidebarItems.length > 0 && mobileMegaItems.length > 0) return;
    let cancelled_1 = false;
    (async () => {
      const data_1 = await getMenuGroups();
      const groups = data_1?.["menu-groups"];
      if (!groups || cancelled_1) return;
      const sidebar = buildMobileMenuTree(groups, "sidebar-menu", "Sidebar Menu");
      const mega = buildMobileMenuTree(groups, "mega-menu", "Mega Menu");
      if (sidebar.length > 0) setMobileSidebarItems(sidebar);
      if (mega.length > 0) setMobileMegaItems(mega);
    })();
    return () => {
      cancelled_1 = true;
    };
  }, []);
  const resolveMenuHref = (item: MenuGroupItem): {
    href: string;
    external: boolean;
  } | null => {
    const raw = item?.url || item?.data?.link;
    if (!raw) return null;

    // Keep full path (e.g., /cinema/cinema-news) instead of just last segment
    const toFullPath = (pathnameValue: string): string => {
      const cleaned_0 = (pathnameValue || "/").split("?")[0].replace(/\/+$/, "");
      return cleaned_0 || "/";
    };
    try {
      const base = typeof window !== "undefined" ? window.location.origin : "https://www.dinamani.com";
      const parsed = new URL(raw, base);
      const isDinamani = parsed.hostname === "www.dinamani.com" || parsed.hostname === "dinamani.com";
      if (isDinamani) {
        return {
          href: toFullPath(parsed.pathname || "/"),
          external: false
        };
      }
      return {
        href: raw,
        external: true
      };
    } catch {
      if (raw.startsWith("http://") || raw.startsWith("https://")) {
        return {
          href: raw,
          external: true
        };
      }
      return {
        href: toFullPath(raw),
        external: false
      };
    }
  };
  useEffect(() => {
    if (!openAuth) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [openAuth]);

  // Scroll listener for fixed nav
  useEffect(() => {
    const handleScroll = () => {
      setIsNavFixed(window.scrollY > 200);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <>
      <header ref={headerRef} className="w-full bg-F8F8F8 shadow-sm">
        {/* Group sites strip - mobile, first row above everything else */}
        <div className="lg:hidden border-b border-DCDCDC bg-F8F8F8 overflow-x-auto">
          <GroupSitesStrip links={cfg.header.groupLinks} className="gap-0 px-2 py-1.5 text-11-english-600 text-1B1B1B" />
        </div>

        {/* TOP STRIP */}

        <div className="relative border-t border-b border-DCDCDC lg:border-DCDCDC">
          <div className="xl:max-w-[1245px] w-full mx-auto">
            {/* center date/time - mobile only; desktop date/time moves next to the group logo below */}
            {cfg.header.dateTime.enabled && <div className="absolute inset-0 flex items-center justify-center pointer-events-none lg:hidden">
                <div className="tracking-wide text-14-english-600 md:text-12-english-600 text-1B1B1B">
                  <span suppressHydrationWarning>{currentTime}</span>
                </div>
              </div>}
            {/* left and right content */}
            <div className="flex items-center justify-start lg:justify-between h-full relative z-10">
              {/* Mobile layout - 2 icons left, 2 icons right */}
              <div className="lg:hidden flex items-center justify-between w-full border-DCDCDC text-h2-english">
                {/* Left side icons - mobile only */}
                <div className="flex items-center">
                  <button onClick={() => {
                  setMenuSource("top");
                  setOpenMenu(!openMenu);
                  setOpenSearch(false);
                  setMobileNavDropdown(null);
                }} aria-label={openMenu ? "Close menu" : "Open menu"} className="p-2 cursor-pointer" style={{
                  backgroundColor: openMenu && menuSource === "top" ? '#012061' : 'transparent'
                }}>
                    <span className={`text-[15px] ${openMenu && menuSource === "top" ? 'text-white' : 'text-1B1B1B'}`}>
                      {openMenu && menuSource === "top" ? <FaTimes /> : <HiMenu />}
                    </span>
                  </button>

                  {/* Search icon - left side mobile */}
                  <button className="p-2 cursor-pointer" style={{
                  backgroundColor: openSearch ? '#012061' : 'transparent'
                }} onClick={toggleSearch} aria-label={openSearch ? "Close search" : "Open search"}>
                    <span className={`text-[13px] ${openSearch ? 'text-white' : 'text-1B1B1B'}`}>
                      {openSearch ? <FaTimes /> : <FaSearch />}
                    </span>
                  </button>
                </div>

                {/* Right side icons - mobile only */}
                <div className="flex items-center">
                  {/* Bookmark icon */}
                  <Link href="/bookmark" className="inline-block text-[16px] p-2 text-1B1B1B hover:text-012061 transition-colors cursor-pointer" aria-label="Bookmarks">
                    <FaBookmark aria-label="Bookmarks" />
                  </Link>

                  {/* User icon */}
                  <button className="inline-block text-[16px] p-2 text-1B1B1B cursor-pointer" onClick={openAuthModal} aria-label="Open login or register" ref={userButtonRefMobile}>
                    <FaUser aria-label="User" />
                  </button>
                </div>
              </div>

              {/* Desktop layout - original horizontal row */}
              <div className="hidden lg:flex items-center border-DCDCDC text-h2-english">
                <button onClick={() => {
                setMenuSource("top");
                setOpenMenu(!openMenu);
                setOpenSearch(false);
                setMobileNavDropdown(null);
                setActiveDropdown(null);
              }} aria-label={openMenu ? "Close menu" : "Open menu"} className="p-2 cursor-pointer" style={{
                backgroundColor: openMenu && menuSource === "top" ? '#012061' : 'transparent'
              }}>
                  <span className={`text-[18px] ${openMenu && menuSource === "top" ? 'text-white' : 'text-1B1B1B'}`}>
                    {openMenu && menuSource === "top" ? <FaTimes /> : <HiMenu />}
                  </span>
                </button>

                {/* Search icon - visible on all screens */}
                <button className="p-2 cursor-pointer" style={{
                backgroundColor: openSearch ? '#012061' : 'transparent'
              }} onClick={toggleSearch} aria-label={openSearch ? "Close search" : "Open search"}>
                  <span className={`text-[16px] ${openSearch ? 'text-white' : 'text-1B1B1B'}`}>
                    {openSearch ? <FaTimes /> : <FaSearch />}
                  </span>
                </button>

                {/* Theme toggle icon */}
                {/* <button
                  className="inline-block text-[16px] p-2 text-1B1B1B hover:text-012061 transition-colors"
                  onClick={toggleTheme}
                  aria-label="Toggle theme"
                 >
                  {theme === 'dark' ? <FaSun aria-label="Switch to light mode" /> : <FaMoon aria-label="Switch to dark mode" />}
                 </button> */}

                {/* Bookmark icon */}
                <Link href="/bookmark" className="inline-block text-[16px] p-2 text-1B1B1B hover:text-012061 transition-colors cursor-pointer" aria-label="Bookmarks">
                  <FaBookmark aria-label="Bookmarks" />
                </Link>

                {/* User icon */}
                <button className="inline-block text-[16px] p-2 text-1B1B1B hover:text-012061 transition-colors cursor-pointer" onClick={openAuthModal} aria-label="Open login or register" ref={userButtonRef}>
                  <FaUser aria-label="User" />
                </button>
              </div>

              {/* Group sites strip - desktop */}
              <GroupSitesStrip links={cfg.header.groupLinks} className="text-12-english-600 text-1B1B1B lg:flex hidden lg:block" />

              {/* hide social icons on mobile */}
              <div className="p-1 hidden lg:block"><SocialIcons useGrid={true} links={cfg.header.socialLinks} /></div>
            </div>
          </div>

        </div>

        {/* MAIN HEADER AREA */}
        <div className="relative bg-F8F8F8">
          <div className="xl:max-w-[1245px] w-full mx-auto p-1">
            <div className="flex items-center justify-between h-[46px] lg:h-[65px]">
              {/* Left brand (Indian Express) - desktop only */}
              <div className="shrink-0 hidden lg:flex items-center gap-3">
                <Link href="/">
                  {/* Above-the-fold header logo — eager load so the flex
                      layout doesn't reflow when w-auto recomputes from the
                      image's intrinsic ratio after a lazy fetch. */}
                  <Image src={resolveAssetUrl(cfg.header.logos.groupLogo.url)} alt={cfg.header.logos.groupLogo.alt} width={50} height={50} className="h-[50px] w-[50px] object-contain" loading="eager" fetchPriority="high" />
                </Link>
                {cfg.header.dateTime.enabled && <span className="tracking-wide text-14-english-600 text-1B1B1B whitespace-nowrap" suppressHydrationWarning>
                    {currentTime}
                  </span>}
              </div>

              {/* Right actions */}
              <div className="hidden lg:flex gap-2 items-end">
                {cfg.header.buttons.map(button => {
                // Prefer the icon synced from Builder; fall back to a
                // built-in icon for the two known default buttons.
                const iconSrc = button.icon ? resolveAssetUrl(button.icon) : HEADER_BUTTON_ICONS[button.id]?.src;
                const external = isExternalUrl(button.url);
                return <Link key={button.id} href={button.url} target={external ? "_blank" : undefined} rel={external ? "noopener noreferrer" : undefined} className="inline-flex items-center gap-2 bg-ECEEF3 text-14-english-700 h-[36px] w-[152px] justify-center text-012061">
                      {iconSrc && <Image src={iconSrc} alt="" width={20} height={20} className="object-contain" loading="lazy" />}
                      {button.label}
                    </Link>;
              })}
              </div>
            </div>

            {/* centered Dinamani logo */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <Link href="/" prefetch className="pointer-events-auto">
                <Image src={resolveAssetUrl(cfg.header.logos.mainLogo.url)} alt={cfg.header.logos.mainLogo.alt} width={150} height={60} className="w-[140px] h-[40px] object-contain lg:w-[150px] lg:h-[60px]" loading="eager" sizes="(max-width: 1024px) 140px, 220px" />
              </Link>
            </div>
          </div>
        </div>

        {/* NAV BAR - RESPONSIVE */}
        <nav className={`border-t border-b-[3px] border-012061 border-x-0 transition-all duration-300 ${isNavFixed ? 'fixed top-0 left-0 right-0 z-[9998] shadow-md bg-F8F8F8' : ''}`}>
          <div className="xl:max-w-[1245px] w-full mx-auto">
            {/* Desktop Menu */}
            <div className="hidden lg:flex items-center justify-between gap-5 text-14-tamil-600 text-020202">
              {desktopMenuItems.slice(0, 9).map(item_0 => {
              const resolved = resolveMenuHref(item_0);
              if (!resolved) return null;
              const active = !resolved.external && isActiveHref(resolved.href);
              const hasChildren = item_0.children && item_0.children.length > 0;
              const isElection2026 = item_0.title === "தேர்தல் 2026";
              const baseClassName = isElection2026 ? "cursor-pointer font-tamil font-bold" : active ? "cursor-pointer font-tamil text-[#215EF9] font-bold" : "cursor-pointer font-tamil hover:text-012061";
              const electionStyle = isElection2026 ? {
                color: '#e5484d'
              } : undefined;

              // Item with submenu
              if (hasChildren) {
                const isDropdownOpen = activeDropdown === item_0.id;
                return <div key={`${item_0.title}-${resolved.href}`} className="relative" onMouseEnter={() => {
                  if (openMenu) setOpenMenu(false);
                  setActiveDropdown(item_0.id);
                }} onMouseLeave={() => setActiveDropdown(null)}>
                      <div className="flex items-center gap-1 py-2 cursor-pointer">
                        {resolved.external ? <Link href={resolved.href} target="_blank" rel="noreferrer" className={baseClassName} style={electionStyle}>
                            {item_0.title}
                          </Link> : <Link href={resolved.href} className={baseClassName} style={electionStyle}>
                            {item_0.title}
                          </Link>}
                        <FaChevronDown className={`text-[10px] text-gray-500 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180 text-012061' : ''}`} />
                      </div>
                      {/* Dropdown */}
                      {isDropdownOpen && <div className="absolute top-full left-0 pt-1 z-50">
                          <div className="bg-white border border-DCDCDC shadow-lg rounded-md min-w-[200px]">
                            <ul className="py-2">
                              {item_0.children!.map(child => {
                          const childResolved = resolveMenuHref(child);
                          if (!childResolved) return null;
                          return <li key={`${child.title}-${childResolved.href}`}>
                                    {childResolved.external ? <Link href={childResolved.href} target="_blank" rel="noreferrer" className="block px-4 py-2 text-13-tamil-500 text-gray-700 hover:bg-gray-100 hover:text-012061 transition-colors">
                                        {child.title}
                                      </Link> : <Link href={childResolved.href} className="block px-4 py-2 text-13-tamil-500 text-gray-700 hover:bg-gray-100 hover:text-012061 transition-colors">
                                        {child.title}
                                      </Link>}
                                  </li>;
                        })}
                            </ul>
                          </div>
                        </div>}
                    </div>;
              }

              // Item without submenu
              if (resolved.external) {
                return <Link key={`${item_0.title}-${resolved.href}`} href={resolved.href} target="_blank" rel="noreferrer" className={baseClassName} style={electionStyle} onClick={() => setOpenMenu(false)}>
                      {item_0.title}
                    </Link>;
              }
              return <Link key={`${item_0.title}-${resolved.href}`} href={resolved.href} className={baseClassName} style={electionStyle} onClick={() => setOpenMenu(false)}>
                    {item_0.title}
                  </Link>;
            })}
              {/* Menu button at end of nav - only show when menu items are loaded */}
              {desktopMenuItems.length > 0 && <button onClick={() => {
              setMenuSource("bottom");
              setOpenMenu(!openMenu);
              setOpenSearch(false);
              setMobileNavDropdown(null);
              setActiveDropdown(null);
            }} aria-label={openMenu ? "Close menu" : "Open menu"} className="p-2 cursor-pointer" style={{
              backgroundColor: openMenu && menuSource === "bottom" ? '#012061' : 'transparent'
            }}>
                  <span className={`text-[15px] flex items-center justify-center ${openMenu && menuSource === "bottom" ? 'text-white' : 'text-1B1B1B'}`}>
                    {openMenu && menuSource === "bottom" ? <FaTimes /> : <FaEllipsisH />}
                  </span>
                </button>}
            </div>
            {/* Mobile Scrollable Menu */}
            <div className="lg:hidden relative" style={{
            backgroundColor: '#012061'
          }} ref={mobileNavDropdownRef}>
              {/* Fixed menu button on left side - only show when menu items are loaded */}
              {desktopMenuItems.length > 0 && <div className="absolute left-0 top-0 h-full flex items-center border-r border-white/20 z-10" style={{
              backgroundColor: '#012061'
            }}>
                  <button onClick={() => {
                setMenuSource("bottom");
                setOpenMenu(!openMenu);
                setOpenSearch(false);
                setMobileNavDropdown(null);
              }} aria-label={openMenu ? "Close menu" : "Open menu"} className="p-3">
                    <span className="text-[15px] text-white flex items-center justify-center">
                      {openMenu && menuSource === "bottom" ? <FaTimes size={15} /> : <FaEllipsisH size={15} />}
                    </span>
                  </button>
                </div>}

              <div className="overflow-x-auto scrollbar-hide">
                <div className="flex gap-2 text-12-tamil-500 text-FFFFFF">
                  {/* Spacer for menu button - only show when menu items are loaded */}
                  {desktopMenuItems.length > 0 && <div className="w-10 shrink-0" />}
                  {/* Menu items */}
                  {desktopMenuItems.slice(0, 9).map(item_1 => {
                  const resolved_0 = resolveMenuHref(item_1);
                  if (!resolved_0) return null;
                  const baseClass = "font-tamil font-semibold whitespace-nowrap p-2";
                  const active_0 = !resolved_0.external && isActiveHref(resolved_0.href);
                  const hasChildren_0 = item_1.children && item_1.children.length > 0;
                  const isDropdownOpen_0 = mobileNavDropdown === item_1.id;
                  const isElection2026_0 = item_1.title === "தேர்தல் 2026";
                  const className = active_0 ? `${baseClass} lg:border-b-4 border-b-2 border-FFFFFF pb-1` : baseClass;
                  const electionStyle_0 = isElection2026_0 ? {
                    color: '#e5484d'
                  } : undefined;

                  // Item with children - show dropdown on click
                  if (hasChildren_0) {
                    return <button key={`${item_1.title}-${resolved_0.href}`} onClick={e => {
                      const button_0 = e.currentTarget;
                      const rect_0 = button_0.getBoundingClientRect();
                      setMobileDropdownPosition(rect_0.left);
                      setMobileNavDropdown(isDropdownOpen_0 ? null : item_1.id);
                      setOpenMenu(false);
                    }} className={`${className} flex items-center gap-1`} style={electionStyle_0}>
                          {item_1.title}
                          <FaChevronDown className={`text-[8px] transition-transform duration-200 ${isDropdownOpen_0 ? 'rotate-180' : ''}`} />
                        </button>;
                  }

                  // Item without children - regular link
                  if (resolved_0.external) {
                    return <Link key={`${item_1.title}-${resolved_0.href}`} href={resolved_0.href} target="_blank" rel="noreferrer" className={className} onClick={() => setOpenMenu(false)} style={electionStyle_0}>
                          {item_1.title}
                        </Link>;
                  }
                  return <Link key={`${item_1.title}-${resolved_0.href}`} href={resolved_0.href} className={className} onClick={() => setOpenMenu(false)} style={electionStyle_0}>
                        {item_1.title}
                      </Link>;
                })}
                  {/* Spacer to prevent overlap with absolute theme toggle */}
                  <div className="w-16 shrink-0" />
                </div>
              </div>

              {/* Mobile dropdown - rendered outside scrollable container */}
              {mobileNavDropdown !== null && (() => {
              const activeItem = desktopMenuItems.find(item_2 => item_2.id === mobileNavDropdown);
              if (!activeItem || !activeItem.children) return null;
              const resolved_1 = resolveMenuHref(activeItem);
              if (!resolved_1) return null;

              // Calculate left position, ensure dropdown doesn't overflow screen
              const dropdownWidth = 200;
              const screenWidth = typeof window !== 'undefined' ? window.innerWidth : 375;
              let leftPos = mobileDropdownPosition;
              // Prevent overflow on right side
              if (leftPos + dropdownWidth > screenWidth) {
                leftPos = screenWidth - dropdownWidth - 8;
              }
              // Prevent negative left
              if (leftPos < 8) {
                leftPos = 8;
              }
              return <div className="absolute top-full z-50 bg-012061 border border-white/20 shadow-lg min-w-[220px] py-2" style={{
                left: `${leftPos}px`
              }}>
                    {/* Parent item as first option */}
                    <Link href={resolved_1.href} className="block px-5 py-3 text-14-tamil-600 text-white hover:bg-white/10 transition-colors border-b border-white/10 font-semibold leading-relaxed" onClick={() => setMobileNavDropdown(null)}>
                      {activeItem.title}
                    </Link>
                    {/* Child items */}
                    {activeItem.children.map(child_0 => {
                  const childResolved_0 = resolveMenuHref(child_0);
                  if (!childResolved_0) return null;
                  return <Link key={`${child_0.title}-${childResolved_0.href}`} href={childResolved_0.href} target={childResolved_0.external ? "_blank" : undefined} rel={childResolved_0.external ? "noreferrer" : undefined} className="block px-5 py-3 text-13-tamil-500 text-white hover:bg-white/10 transition-colors leading-relaxed" onClick={() => setMobileNavDropdown(null)}>
                          {child_0.title}
                        </Link>;
                })}
                  </div>;
            })()}

              {/* Fixed theme toggle icon on right side */}
              {/* <div
                className="absolute right-0 top-0 h-full flex items-center px-4 border-l border-white/20"
                style={{ backgroundColor: '#012061' }}
               >
                <button
                  className="text-white hover:text-gray-200 transition-colors"
                  onClick={toggleTheme}
                  aria-label="Toggle theme"
                >
                  {theme === 'dark' ? <FaSun aria-label="Switch to light mode" /> : <FaMoon aria-label="Switch to dark mode" />}
                </button>
               </div> */}
            </div>
          </div>

        </nav>
      </header>

      {/* MEGA MENU OVERLAY */}
      {openMenu && (!isDesktop ? (/* Mobile: hamburger (top) shows sidebar-menu items, 3-dot (bottom) shows mega-menu items */
    <div ref={megaMenuRef} className="fixed inset-0 z-[9999]" style={{
      backgroundColor: '#FFFFFF'
    }}>
            <div className="h-full overflow-y-auto">
              {/* Header */}
              <div className="sticky top-0 z-10 p-4 border-b border-DCDCDC" style={{
          backgroundColor: '#F8F8F8'
        }}>
                <div className="flex items-center justify-between">
                  <Image src={resolveAssetUrl(cfg.header.logos.mainLogo.url)} alt={cfg.header.logos.mainLogo.alt} width={120} height={40} className="w-auto h-10" loading="lazy" />
                  <button onClick={() => setOpenMenu(false)} className="p-2 rounded-full transition-colors bg-ECEEF3 hover:bg-gray-200" aria-label="Close menu">
                    <FaTimes className="text-1B1B1B" size={16} />
                  </button>
                </div>
              </div>
              {/* Menu items */}
              <div className="p-4">
                <MobileMenuList items={menuSource === "bottom" ? mobileMegaItems : mobileSidebarItems} resolveHref={resolveMenuHref} onNavigate={() => setOpenMenu(false)} />
              </div>
            </div>
          </div>) : (/* Hamburger (top) + Desktop: use existing MegaMenu component (sidebar-menu API) */
    <div ref={megaMenuRef} className={isNavFixed ? 'fixed top-[41px] left-0 right-0 z-[9997]' : ''}>
            <MegaMenu onClose={() => setOpenMenu(false)} isDesktop={isDesktop} menuSource={menuSource} serverMenuData={serverMenuData} isNavFixed={isNavFixed} />
          </div>))}

      {/* SEARCH OVERLAY */}
      {openSearch && <div ref={searchRef} className={isDesktop ? "relative inset-0 z-[9]" : "fixed inset-0 z-[9999] bg-FFFFFF"}>
          <div className={isDesktop ? "absolute top-4 left-4 right-4 mx-auto max-w-6xl" : "absolute inset-0"} style={isDesktop ? {
        marginTop: '-8.5rem'
      } : {}}>
            <div className={isDesktop ? "bg-FFFFFF border border-DCDCDC shadow-lg" : "h-full overflow-y-auto relative"} style={isDesktop ? {
          maxHeight: '80vh',
          overflowY: 'auto'
        } : {}}>
              {/* Close button - matches MegaMenu style */}
              {!isDesktop && <button onClick={closeSearch} className="absolute top-4 right-4 z-10 p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors" aria-label="Close search">
                  <FaTimes className="text-808080-700" size={20} />
                </button>}

              <div className={isDesktop ? "" : ""}>
                <SearchLayoutSection onClose={closeSearch} trendingTags={trendingTags} />
              </div>
            </div>
          </div>
        </div>}

      {openAuth && <div className="fixed inset-0 z-[9999]">
          <button className="absolute inset-0 bg-black/50" aria-label="Close login or register" onClick={closeAuthModal} />
          {authPopup === "profile" && profileMenuPos ? <div className="absolute" style={{
        top: profileMenuPos.top,
        left: profileMenuPos.left,
        width: "min(260px, calc(100vw - 20px))"
      }}>
              <div className="overflow-hidden shadow-lg border border-DCDCDC bg-FFFFFF">
                <ProfileMenu />
              </div>
            </div> : <div className="absolute inset-0 flex items-stretch justify-center p-[10px] sm:items-center sm:p-6 md:p-8 lg:p-12">
              <div className="relative w-full h-[calc(100dvh-20px)] overflow-x-hidden overflow-y-auto sm:h-auto sm:max-h-[92vh] sm:max-w-md lg:max-w-[537px] sm:shadow-lg">
                {authPopup === "login" ? <LoginRegisterForm onClose={closeAuthModal} onContinue={goToOtp} /> : authPopup === "otp" ? <OTPForm onClose={closeAuthModal} onSuccess={handleAuthSuccess} /> : <ProfileMenu />}
              </div>
            </div>}
        </div>}
    </>;
}

// Memoize to prevent unnecessary re-renders
export default memo(HeaderComponent);